import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DeleteVideoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  video: {
    id: string;
    title: string;
  } | null;
}

export const DeleteVideoDialog = ({ 
  isOpen, 
  onOpenChange, 
  onSuccess,
  video 
}: DeleteVideoDialogProps) => {
  const { t } = useTranslation('presentation');
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!video) return;
    
    setIsDeleting(true);

    try {
      const { error } = await supabase
        .from('presentation_videos')
        .delete()
        .eq('id', video.id);

      if (error) throw error;

      toast({
        title: t('messages.videoDeleted'),
        description: t('messages.videoDeletedDesc'),
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: t('messages.error'),
        description: t('messages.errorDeleting'),
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deleteVideo.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('deleteVideo.description', { title: video?.title })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>{t('deleteVideo.cancel')}</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete} 
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? t('deleteVideo.deleting') : t('deleteVideo.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
